import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { logger } from '../utils/logger';
import './AuthCallback.css';

// Token response from Cognito
interface TokenResponse {
  id_token: string;
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

const AuthCallback: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  // Guard against React StrictMode double-invoking effects, which would
  // consume the one-time-use authorization code twice and cause invalid_grant.
  const processedCodeRef = useRef<string | null>(null);

  useEffect(() => {
    const exchangeCodeForTokens = async (code: string): Promise<TokenResponse> => {
      const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN;
      const clientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
      const redirectUri = `${window.location.origin}/auth/callback`;

      if (!cognitoDomain || !clientId) {
        throw new Error('Cognito configuration missing');
      }

      const tokenEndpoint = `${cognitoDomain}/oauth2/token`;

      const params = new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: clientId,
        code: code,
        redirect_uri: redirectUri,
      });

      const response = await fetch(tokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      });

      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Token exchange failed: ${response.status} - ${errorData}`);
      }

      return response.json();
    };

    const handleCallback = async () => {
      const urlParams = new URLSearchParams(location.search);
      const code = urlParams.get('code');
      const urlError = urlParams.get('error');
      const errorDescription = urlParams.get('error_description');

      if (urlError) {
        setError(`${urlError}${errorDescription ? `: ${errorDescription}` : ''}`);
        return;
      }

      if (!code) {
        setError('No authorization code received');
        return;
      }

      // Prevent the code from being exchanged more than once.
      // React StrictMode mounts -> unmounts -> re-mounts in dev, causing
      // the effect to fire twice with the same code, exhausting the
      // single-use grant and producing "invalid_grant".
      if (processedCodeRef.current === code) {
        return;
      }
      processedCodeRef.current = code;

      try {
        // Exchange the code for tokens (silent - no UI update)
        const tokens = await exchangeCodeForTokens(code);

        // Store tokens in localStorage
        localStorage.setItem('idToken', tokens.id_token);
        localStorage.setItem('accessToken', tokens.access_token);
        localStorage.setItem('refreshToken', tokens.refresh_token);

        // Calculate and store token expiry
        const expiresAt = Date.now() + tokens.expires_in * 1000;
        localStorage.setItem('tokenExpiresAt', expiresAt.toString());

        // Parse user info from ID token
        try {
          const payload = JSON.parse(atob(tokens.id_token.split('.')[1]));
          localStorage.setItem('userEmail', payload.email || '');
          localStorage.setItem('userId', payload['custom:user_id'] || payload.sub);
          localStorage.setItem(
            'displayName',
            payload['custom:display_name'] || payload.email?.split('@')[0] || '',
          );

          // Check if federated user is missing age range via backend profile
          const ageRangeFromToken = payload['custom:age_range'];
          if (!ageRangeFromToken) {
            try {
              const apiUrl = process.env.REACT_APP_API_URL?.replace(/\/$/, '') ?? '';
              const profileResponse = await fetch(`${apiUrl}/user/profile`, {
                method: 'GET',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${tokens.id_token}`,
                },
              });
              if (profileResponse.ok) {
                const profileData = await profileResponse.json();
                if (!profileData.profile?.age_range) {
                  sessionStorage.setItem('theychat-needs-age-range', 'true');
                }
              } else {
                // Profile not found (new user) — prompt for age range
                sessionStorage.setItem('theychat-needs-age-range', 'true');
              }
            } catch (_profileErr: unknown) {
              // If profile check fails, prompt to be safe
              logger.error(
                'AuthCallback',
                'Failed to check user profile for age range',
                _profileErr as Error,
              );
              sessionStorage.setItem('theychat-needs-age-range', 'true');
            }
          }
        } catch (_e: unknown) {
          logger.error('AuthCallback', 'Failed to parse ID token payload', _e as Error);
        }

        // Redirect to the return URL (stored before Google OAuth redirect) or home
        const returnUrl = sessionStorage.getItem('theychat-auth-return-url') || '/';
        sessionStorage.removeItem('theychat-auth-return-url');
        navigate(returnUrl, { replace: true });
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to complete login';
        setError(message);
      }
    };

    handleCallback();
  }, [location, navigate]);

  // Only show UI if there's an error - otherwise it's a seamless redirect
  if (error) {
    return (
      <div className="auth-callback-error">
        <div className="auth-callback-card">
          <div className="auth-callback-icon">⚠️</div>
          <h2>Authentication Failed</h2>
          <p>{error}</p>
          <button type="button" onClick={() => navigate('/login', { replace: true })}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Minimal loading state - only shown briefly during token exchange
  return (
    <div className="auth-callback-loading">
      <div className="auth-callback-spinner" />
    </div>
  );
};

export default AuthCallback;
